import { yupResolver } from '@hookform/resolvers/yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { ERole } from '../../../../shared/enums/user/ERole';
import { handleError } from '../../../../shared/functions/handleError';
import { ILoginUser, IUser } from '../../../../shared/interfaces/user/IUser';
import authSlice from '../../../../store/slices/auth.slice';
import moduleSlice from '../../../../store/slices/module.slice';
import { AuthService } from '../../service';

const schema = yup.object().shape({
  email: yup.string().email().required(),
  password: yup.string().min(8).max(32).required(),
});
const LoginForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { handleSubmit, control } = useForm<ILoginUser>({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(schema),
  });
  const [showPassword, setShowPassword] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = (formState: ILoginUser) => {
    AuthService.login(formState)
      .then((response) => {
        if (response) {
          const currentUser = response?.userData as IUser;
          dispatch(
            authSlice.actions.setLoginState({
              ...response,
              isAuthenticated: true,
            }),
          );
          dispatch(
            moduleSlice.actions.setModuleFilters({
              updatedAt: new Date()?.getTime(),
            }),
          );
          navigate(
            currentUser?.role === ERole.USER
              ? '/user/dashboard'
              : '/admin/dashboard',
          );
          enqueueSnackbar(t('toasts.successfullyLoggedIn'), {
            variant: 'success',
          });

          localStorage.setItem('token', response?.token);
        }
      })
      .catch((err) => handleError(err));
  };
  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{
        width: '75%',
      }}
    >
      <Typography variant="h4">{t('general.login.welcome')} 👋</Typography>

      <Typography variant="subtitle1">
        {t('general.login.pleaseLoginHere')}
      </Typography>
      <Box sx={{ margin: '20px 0' }}>
        <Controller
          name={'email'}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              error={!!error?.message}
              helperText={error?.message}
              value={value ?? ''}
              onChange={onChange}
              label={t('general.login.email')}
              sx={{ width: '100%' }}
            />
          )}
        />
      </Box>
      <Box
        sx={{
          marginBottom: '10px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Controller
          name={'password'}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              error={!!error?.message}
              helperText={error?.message}
              value={value ?? ''}
              onChange={onChange}
              type={showPassword ? 'text' : 'password'}
              label="Password"
              sx={{ width: '100%' }}
            />
          )}
        />
        <Box
          sx={{ marginLeft: '-40px', position: 'relative', cursor: 'pointer' }}
        >
          {showPassword ? (
            <VisibilityOff onClick={() => setShowPassword((prev) => !prev)} />
          ) : (
            <Visibility onClick={() => setShowPassword((prev) => !prev)} />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        <Typography
          variant="subtitle1"
          color="primary"
          className={'cursor-pointer w-fit'}
          onClick={() => navigate('/forgot-password')}
        >
          {t('general.login.forgotPassword')}
        </Typography>
      </Box>
      <Button
        variant={'contained'}
        sx={{ width: '100%', marginTop: '20px' }}
        type={'submit'}
      >
        {t('button.login')}
      </Button>
    </form>
  );
};

export default LoginForm;
